import React from "react";
import PropTypes from "prop-types";
import _ from 'lodash';

import ProductImageCarouselWrapper from '../../shared/ProductImageCarouselWrapper';
import TraitColorPicker from '../../shared/TraitColorPicker';
import RelatedProductsWrapper from './RelatedProductsWrapper';
import ReicoExpressFilter from '../../shared/ReicoExpressFilter';
import ProductDetailTabs from '../../shared/ProductDetailTabs';

const variationToImages = (variation) => ({
    large: variation.LargeImages,
    primary: variation.PrimaryImages,
    name: variation.Name,
    code: variation.Code
});

class ProductComponents extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.state.filterForReicoExpress = this.props.filterForReicoExpress;
        this.state.quantity = 1;

        this.initialState = this.state;

        this.updateSelectedSku = this.updateSelectedSku.bind(this);
        this.findTraitValueIndexes = this.findTraitValueIndexes.bind(this);
        this.computeSelectionStateUpdate = this.computeSelectionStateUpdate.bind(this);
        this.onFilterReicoExpressChange = this.onFilterReicoExpressChange.bind(this);

        this.onTraitSelectedFns = this.props.traits.map((trait, i) => this.makeTraitSelectionListener({
            dim: i
        }).bind(this));
    }

    onQuantityUpdate(val) {
        var parsed = Number.parseInt(val, 10);
        if (Number.isNaN(parsed) || parsed < 1) {
            this.setState({ quantity: 1 });
        } else {
            this.setState({ quantity: parsed });
        }
    }

    findTraitValueIndexes(sku) {
        var ret = [];
        let variation = this.props.variations.find(variation => variation.Code === sku);
        if (typeof variation !== 'undefined') {
            for (let trait of this.props.traits) {
                ret.push(trait.values.indexOf(variation[trait.valueKey]));
            }
        }
        return ret;
    }

    computeSelectionStateUpdate(dim, index, traitSelections, traitIndexes, traits, variations) {
        traitSelections = traitSelections.slice();

        if (Number.isNaN(index)) {
            traitSelections = this.initialTraitSelections;
            traitIndexes[dim] = null;
        } else {
            traitSelections[dim] = index;
            traitIndexes[dim] = index;
        }

        const selectedValues = traitSelections.map((index, i) => index === null ? null : traits[i].values[index]);

        // filter for the variations that match all the currently selected trait values
        const filteredVariations = variations.filter(
            variation => traits.every(                     // look at each trait for each variation to see 
                (trait, i) => selectedValues[i] === null || variation[trait.valueKey] === selectedValues[i]));

        if (filteredVariations.length === 1) {
            // One match. Clear the filters, select all the trits for this variation
            const selectedSku = filteredVariations[0].Code;
            // BBS DDD We really shouldn't be calling out to the event receiver here, but instead indicating
            // the new sku as a part of our return value. Whatever. get it done. (2018-05-03)
            this.props.onSelectVariation(selectedSku);
            return {
                traitMasks: this.initialTraitMasks,
                traitSelections: this.initialTraitSelections,
                traitIndexes: this.findTraitValueIndexes(selectedSku)
            };
        }

        if (filteredVariations.length === 0) {
            // No matches. Clear selections and filters.
            return this.initialState;
        }

        // Multiple matches. Figure out what trait values to show based on what the user has already chosen
        const traitMasks = traits.map((trait, i) => {
            if (traitSelections[i] === null) {
                // No selection, so get the values for this trait from the remaining variations 
                const remainingValues = filteredVariations.map(variation => variation[trait.valueKey]);
                return trait.values.map(value => remainingValues.includes(value));
            }
            // user already made selection, so show all in case they want to change
            return trait.values.map(val => true);
        });

        const filteredSkus = filteredVariations.map(variation => variation.Code);
        if (!filteredSkus.includes(this.props.currentVariation.Code)) {
            const selectedSku = filteredVariations[0].Code;
            this.props.onSelectVariation(selectedSku);
        }

        return { traitMasks, traitSelections, traitIndexes };
    }

    makeTraitSelectionListener({ dim }) {
        const self = this;
        return function onTraitIndexSelected(index) {
            self.setState(this.computeSelectionStateUpdate(dim, index, this.state.traitSelections, this.state.traitIndexes, this.props.traits, this.props.variations));
        };
    }

    updateSelectedSku(sku) {
        if (sku === null) {
            return;
        }

        const values = this.findTraitValueIndexes(sku);
        const stateUpdate = {
            traitMasks: this.initialTraitMasks,
            traitSelections: this.initialTraitSelections,
            traitIndexes: values
        };

        this.setState(stateUpdate);
        this.props.onSelectVariation(sku);
    }

    onFilterReicoExpressChange() {
        this.setState((prevState) => {
            return {
                filterForReicoExpress: !prevState.filterForReicoExpress
            };
        });

        if (!this.state.filterForReicoExpress && !this.props.currentVariation.IsReicoExpress) {
            var firstReicoExpressVariant = this.props.variations.find(variation => (variation.IsReicoExpress || false));
            this.updateSelectedSku(firstReicoExpressVariant.Code);
        }
    }

    render() {
        const { assets, blogs, currentVariation, dealerDocuments, documentWarranties, gallery, videos, traits, variations, isColorPicker } = this.props;
        const hasTrait0Values = typeof traits[0] !== 'undefined',
            hasTrait1Values = typeof traits[1] !== 'undefined';

        var layout = {
            showTrait0Dropdown: hasTrait0Values && (hasTrait1Values || !isColorPicker),
            showTrait1Dropdown: hasTrait1Values && !isColorPicker,
            allAttributes: currentVariation.VariationAttributes,
            showQuoteButton: currentVariation.ShowQuoteButton,
            showBuyButton: currentVariation.ShowBuyButton,
            showSkuSelector: variations.length > 1
        };
        const reicoExpressVariations = variations.filter(variation => (variation.IsReicoExpress || false));
        const filteredVariationsForReicoExpress = this.state.filterForReicoExpress
            ? reicoExpressVariations
            : variations;

        return (
            <div className='product-wrapper cabinet-product-wrapper'>
                <div className="container">
                    <div className='product-details-wrapper'>
                        <div className='row'>
                            <div className='col-xs-12 col-sm-6 col-md-4'>
                                <div className='row'>
                                    <div className='col-xs-12'>
                                        <ProductImageCarouselWrapper images={variationToImages(currentVariation)} fallbackTitle={currentVariation.Name} />
                                    </div>
                                    <div className='col-xs-12'>
                                        <ReicoExpressFilter handleChange={this.onFilterReicoExpressChange} isEnabled={reicoExpressVariations.length > 0} isActive={this.state.filterForReicoExpress} />
                                    </div>
                                    <div className='col-xs-12'>
                                        <TraitColorPicker onSelect={this.updateSelectedSku} variations={filteredVariationsForReicoExpress} currentVariation={currentVariation} />
                                    </div>
                                </div>
                            </div>
                            <div className='col-xs-12 col-sm-6 col-md-8'>
                                <h1>{currentVariation.Name}</h1>
                                <ProductDetailTabs
                                    assets={assets}
                                    blogs={blogs}
                                    currentVariation={currentVariation}
                                    documentWarranties={documentWarranties}
                                    gallery={gallery}
                                    id="product-detail-tabs"
                                    resources={dealerDocuments}
                                    videos={videos}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <RelatedProductsWrapper
                    addToCartEndpoint={this.props.addToCartEndpoint}
                    categoryId={this.props.categoryId}
                    currentVariation={currentVariation}
                    filterForReicoExpress={this.state.filterForReicoExpress}
                    siteInfo={this.props.siteInfo}
                    customerType={this.props.customerType}
                    siteName={this.props.siteName}
                    wishlistModel={this.props.wishlistModel}
                    updateWishlists={this.props.updateWishlists}
                />
            </div>
        );
    }
}

ProductComponents.propTypes = {
    currentVariation: PropTypes.object.isRequired,
    variations: PropTypes.array.isRequired,
    onSelectVariation: PropTypes.func.isRequired,
    isColorPicker: PropTypes.bool.isRequired,
    assets: PropTypes.array,
    gallery: PropTypes.object,
    videos: PropTypes.object,
    documentWarranties: PropTypes.object,
    dealerDocuments: PropTypes.array,
    blogs: PropTypes.object,
    relatedProducts: PropTypes.array,
    categoryId: PropTypes.number,
    addToCartEndpoint: PropTypes.string,
    filterForReicoExpress: PropTypes.bool,
    siteInfo: PropTypes.shape({
        siteName: PropTypes.string,
        phoneNumber: PropTypes.string,
        phoneText: PropTypes.string
    }),
    customerType: PropTypes.string,
    siteName: PropTypes.string,
    wishlistModel: PropTypes.object
};

ProductComponents.defaultProps = {
    assets: [],
    relatedProducts: []
};

export default ProductComponents;